import React, { useEffect, useState } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro";
import { SectionHeading, Subheading as SubheadingBase } from "components/misc/Headings";
import { Container as ContainerBase, ContentWithPaddingXl } from "components/misc/Layouts";
import { SectionDescription } from "components/misc/Typography";

import projectsbg from "images/yenimahalle/projects_bg.jpg";
import PortalApi from "api/PortalApi";

// AnimatedNumber
interface AnimatedNumberProps {
  value: string | number;
  duration?: number;
}

const AnimatedNumber: React.FC<AnimatedNumberProps> = ({ value, duration = 2 }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let start = 0;
    const end = typeof value === "string" ? parseInt(value, 10) : value;
    if (isNaN(end) || start === end) return;

    const totalMs = duration * 1000;
    const incrementTime = totalMs / end;

    const timer = setInterval(() => {
      start += 1;
      setDisplayValue(start);
      if (start >= end) {
        clearInterval(timer);
      }
    }, incrementTime);

    return () => clearInterval(timer);
  }, [value, duration]);

  return <>{displayValue}</>;
};

// Types
interface StatItem {
  key: string;
  value: string;
  link: string;
}

interface ProjectsSectionProps {
  subheading?: string;
  heading?: string;
  description?: string;
}

// Styled Components
const Container = styled(ContainerBase)`
  ${tw`my-8 lg:my-10 text-gray-100 -mx-8 px-8 relative overflow-hidden`}
  background: 
    linear-gradient(
      rgba(0,0,0,0.5), 
      rgba(0,0,0,0.5)
    ),
    url(${projectsbg});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

const HeadingContainer = tw.div``;
const Heading = tw(SectionHeading)`sm:text-3xl md:text-4xl lg:text-5xl`;
const Subheading = tw(SubheadingBase)`text-gray-100 text-center`;
const Description = tw(SectionDescription)`text-gray-400 text-center mx-auto max-w-screen-md`;

const StatsContainer = tw.div`
  mt-8 flex flex-col sm:flex-row 
  items-center justify-center flex-wrap 
  max-w-screen-md 
  justify-between mx-auto
`;

const StatLink = styled.a`
  ${tw`
    flex flex-col text-center p-4 tracking-wide
    no-underline cursor-pointer
    transition duration-200
    hover:bg-white hover:bg-opacity-10
    rounded
    m-2
    text-white
  `}
`;

const StatValue = tw.div`text-4xl sm:text-3xl md:text-4xl lg:text-5xl font-black`;
const StatKey = tw.div`text-xl font-medium`;

// Bileşen
const ProjectsSection: React.FC<ProjectsSectionProps> = ({
  subheading = "",
  heading = "PROJELERİMİZ",
  description = "Yenimahalle için planladığımız, hizmete açılan ve yapımı devam eden projelerimiz.",
}) => {
  const [stats, setStats] = useState<StatItem[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await PortalApi.getHomePageData();
        if (response) {
          const stat1 = {
            key: "Devam Eden Projeler",
            value: response.ongoingProjects.toString(),
            link: "/Projelerimiz/devam-eden-projeler/2",
          };
          const stat2 = {
            key: "Tamamlanan Projeler",
            value: response.complatedProjects.toString(),
            link: "/Projelerimiz/tamamlananan-projeler/1",
          };

          setStats([stat1, stat2]);
        }
      } catch (error) {
        console.error("Projeler alınırken hata:", error);
      }
    };

    fetchData();
  }, []);

  return (
    <Container>
      <ContentWithPaddingXl>
        <HeadingContainer>
          {subheading && <Subheading>{subheading}</Subheading>}
          <Heading>{heading}</Heading>
          {description && <Description>{description}</Description>}
        </HeadingContainer>
        <StatsContainer>
          {stats.map((stat, index) => (
            <StatLink key={index} href={stat.link}>
              <StatValue>
                <AnimatedNumber value={stat.value} duration={2} />
              </StatValue>
              <StatKey>{stat.key}</StatKey>
            </StatLink>
          ))}
        </StatsContainer>
      </ContentWithPaddingXl>
    </Container>
  );
};

export default ProjectsSection;
